import { create } from 'zustand'
import type { DateRange, LegacyDateRange, RelativeDateRange, AbsoluteDateRange } from '@/shared/types';
import { getDateRangeAsDates } from '@/shared/utils';

interface DateRangeStore {
  dateRange: DateRange;
  setDateRange: (dateRange: DateRange) => void;
  setRelativeDateRange: (value: number, unit: 'hours' | 'days', label: string) => void;
  setAbsoluteDateRange: (from: Date, to: Date) => void;
  getCurrentDateRange: () => LegacyDateRange;
  isInitialized: boolean;
  setInitialized: (value: boolean) => void;
}

// Get stored date range
const getStoredDateRange = (): DateRange | null => {
  const stored = localStorage.getItem('dateRange');
  if (!stored) return null;
  try {
    const parsed = JSON.parse(stored);
    
    // Handle new format with mode
    if (parsed.mode) {
      if (parsed.mode === 'relative') {
        return {
          mode: 'relative',
          value: parsed.value,
          unit: parsed.unit,
          label: parsed.label
        };
      } else if (parsed.mode === 'absolute') {
        return {
          mode: 'absolute',
          from: new Date(parsed.from),
          to: new Date(parsed.to)
        };
      }
    }
    
    return null;
  } catch (error) {
    console.error('Error parsing stored date range:', error);
    return null;
  }
};

export const defaultDateRange: DateRange = {
  mode: 'relative',
  value: 7,
  unit: 'days',
  label: 'Last 7 days'
};

export const useDateRangeStore = create<DateRangeStore>((set, get) => ({
  dateRange: getStoredDateRange() || defaultDateRange,
  isInitialized: false,
  setInitialized: (value: boolean) => set({ isInitialized: value }),
  
  getCurrentDateRange: () => {
    const currentDateRange = get().dateRange;
    return getDateRangeAsDates(currentDateRange);
  },
  
  setRelativeDateRange: (value: number, unit: 'hours' | 'days', label: string) => {
    const newRange: RelativeDateRange = {
      mode: 'relative',
      value,
      unit,
      label
    };
    
    // Store in localStorage
    localStorage.setItem('dateRange', JSON.stringify(newRange));
    set({ dateRange: newRange });
  },
  
  setAbsoluteDateRange: (from: Date, to: Date) => {
    // Validate input dates
    if (!from || !to || isNaN(from.getTime()) || isNaN(to.getTime())) {
      console.error('Invalid date range provided:', { from, to });
      return;
    }
    
    // Ensure dates are created in UTC
    const newRange: AbsoluteDateRange = {
      mode: 'absolute',
      from: new Date(Date.UTC(
        from.getUTCFullYear(),
        from.getUTCMonth(),
        from.getUTCDate(),
        from.getUTCHours(),
        from.getUTCMinutes(),
        0,
        0
      )),
      to: new Date(Date.UTC(
        to.getUTCFullYear(),
        to.getUTCMonth(),
        to.getUTCDate(),
        to.getUTCHours(),
        to.getUTCMinutes(),
        59,
        999
      ))
    };
    
    // Validate the constructed dates
    if (isNaN(newRange.from.getTime()) || isNaN(newRange.to.getTime())) {
      console.error('Failed to create valid UTC dates:', { from, to });
      return;
    }
    
    // Store in localStorage
    localStorage.setItem('dateRange', JSON.stringify({
      mode: 'absolute',
      from: newRange.from.toISOString(),
      to: newRange.to.toISOString()
    }));
    
    set({ dateRange: newRange });
  },
  
  setDateRange: (dateRange: DateRange) => {
    // Store in localStorage
    if (dateRange.mode === 'relative') {
      localStorage.setItem('dateRange', JSON.stringify(dateRange));
    } else if (dateRange.mode === 'absolute') {
      localStorage.setItem('dateRange', JSON.stringify({
        mode: 'absolute',
        from: dateRange.from.toISOString(),
        to: dateRange.to.toISOString()
      }));
    }
    
    set({ dateRange });
  },
}));
